import { Minus, Plus } from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Switch, useWindowDimensions, View } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { FormInput } from '../../../components/primitives/FormInput';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useSaveVoicePreview, useVoicePreview } from '../hooks/useVoice';
import type { CreateVoiceTemplateInput, UpdateVoiceTemplateInput, VoiceSettings } from '../types';
import { VoicePreviewPlayer } from './VoicePreviewPlayer';
import { VoiceSelector } from './VoiceSelector';

interface SimpleSliderProps {
  value: number;
  onValueChange: (value: number) => void;
  minimumValue: number;
  maximumValue: number;
  step: number;
  minimumTrackTintColor: string;
  maximumTrackTintColor: string;
}

interface VoiceTemplateFormProps {
  initialData?: {
    name: string;
    description?: string;
    scriptTemplate: string;
    voiceId: string;
    voiceSettings?: VoiceSettings;
    isActive?: boolean;
    previewAudioUrl?: string | null;
  };
  organizationId: string;
  templateId?: string;
  onSubmit: (data: CreateVoiceTemplateInput | UpdateVoiceTemplateInput) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  isEdit?: boolean;
}

function SimpleSlider({
  value,
  onValueChange,
  minimumValue,
  maximumValue,
  step,
  minimumTrackTintColor,
  maximumTrackTintColor,
}: Readonly<SimpleSliderProps>) {
  const percentage = ((value - minimumValue) / (maximumValue - minimumValue)) * 100;

  const adjustValue = (delta: number) => {
    const newValue = Math.max(minimumValue, Math.min(maximumValue, value + delta));
    onValueChange(Math.round(newValue / step) * step);
  };

  return (
    <HStack spacing="sm" align="center">
      <Pressable
        onPress={() => adjustValue(-step * 10)}
        style={styles.sliderAdjustButton}
        accessibilityRole="button"
        accessibilityLabel="Decrease value"
      >
        <Minus size={14} color={minimumTrackTintColor} strokeWidth={2.5} />
      </Pressable>

      <View style={styles.sliderTrackOuter}>
        <View
          style={[
            styles.sliderTrackInner,
            { width: `${percentage}%`, backgroundColor: minimumTrackTintColor },
          ]}
        />
        <View
          style={[
            styles.sliderTrackRemainder,
            { width: `${100 - percentage}%`, backgroundColor: maximumTrackTintColor },
          ]}
        />
      </View>

      <Pressable
        onPress={() => adjustValue(step * 10)}
        style={styles.sliderAdjustButton}
        accessibilityRole="button"
        accessibilityLabel="Increase value"
      >
        <Plus size={14} color={minimumTrackTintColor} strokeWidth={2.5} />
      </Pressable>
    </HStack>
  );
}

export function VoiceTemplateForm({
  initialData,
  organizationId,
  templateId,
  onSubmit,
  onCancel,
  isSubmitting = false,
  isEdit = false,
}: Readonly<VoiceTemplateFormProps>) {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;

  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [scriptTemplate, setScriptTemplate] = useState(initialData?.scriptTemplate || '');
  const [voiceId, setVoiceId] = useState(initialData?.voiceId || '');
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>(
    initialData?.voiceSettings || {
      stability: 0.5,
      similarityBoost: 0.75,
      style: 0,
      useSpeakerBoost: true,
    },
  );

  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [savedPreviewUrl, setSavedPreviewUrl] = useState<string | null>(
    initialData?.previewAudioUrl || null,
  );

  const previewMutation = useVoicePreview();
  const savePreviewMutation = useSaveVoicePreview();

  const TEMPLATE_VARIABLES = useMemo(
    () => [
      { label: t('voice.form.variables.leadName'), value: '{{lead.fullName}}' },
      { label: t('voice.form.variables.leadEmail'), value: '{{lead.email}}' },
      { label: t('voice.form.variables.company'), value: '{{lead.company}}' },
      { label: t('voice.form.variables.userName'), value: '{{user.fullName}}' },
      { label: t('voice.form.variables.organization'), value: '{{organization.name}}' },
    ],
    [t],
  );

  const handleInsertVariable = useCallback((variable: string) => {
    setScriptTemplate((prev) => `${prev}${prev ? ' ' : ''}${variable}`);
  }, []);

  const handlePreview = useCallback(async () => {
    if (!voiceId || !scriptTemplate) return;

    const previewText = scriptTemplate
      .replaceAll('{{lead.fullName}}', 'John Doe')
      .replaceAll('{{lead.email}}', 'john@example.com')
      .replaceAll('{{lead.company}}', 'Acme Corp')
      .replaceAll('{{user.fullName}}', 'Sales Rep')
      .replaceAll('{{organization.name}}', 'Your Company')
      .substring(0, 500);

    try {
      if (isEdit && templateId) {
        const result = await savePreviewMutation.mutateAsync({
          voiceId,
          text: previewText,
          voiceSettings,
          templateId,
          organizationId,
          saveToTemplate: true,
        });
        setSavedPreviewUrl(result.previewAudioUrl);
        setAudioBlob(null);
      } else {
        const blob = await previewMutation.mutateAsync({
          voiceId,
          text: previewText,
          voiceSettings,
        });
        setAudioBlob(blob);
      }
    } catch (error) {
      console.error('Preview failed:', error);
    }
  }, [
    isEdit,
    organizationId,
    previewMutation,
    savePreviewMutation,
    scriptTemplate,
    templateId,
    voiceId,
    voiceSettings,
  ]);

  const handleSubmit = useCallback(() => {
    if (!name || !scriptTemplate || !voiceId) return;

    const data = isEdit
      ? ({
          name,
          description: description || undefined,
          scriptTemplate,
          voiceId,
          voiceSettings,
          isActive,
        } as UpdateVoiceTemplateInput)
      : ({
          organizationId,
          name,
          description: description || undefined,
          scriptTemplate,
          voiceId,
          voiceSettings,
          isActive,
        } as CreateVoiceTemplateInput);

    onSubmit(data);
  }, [
    description,
    isActive,
    isEdit,
    name,
    onSubmit,
    organizationId,
    scriptTemplate,
    voiceId,
    voiceSettings,
  ]);

  const characterCount = scriptTemplate.length;
  const isValid = Boolean(name && scriptTemplate && voiceId);
  const isGeneratingPreview = previewMutation.isPending || savePreviewMutation.isPending;
  const stability = voiceSettings.stability ?? 0.5;
  const similarityBoost = voiceSettings.similarityBoost ?? 0.75;

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <VStack spacing="lg">
        <Box style={styles.sectionCard}>
          <VStack spacing="md">
            <Text variant="label" color={colors.text.secondary}>
              {t('voice.form.name.label')} *
            </Text>
            <Box style={styles.inputContainer}>
              <FormInput
                value={name}
                onChangeText={setName}
                placeholder={t('voice.form.name.placeholder')}
                style={styles.input}
              />
            </Box>

            <Text variant="label" color={colors.text.secondary}>
              {t('voice.form.description.label')}
            </Text>
            <Box style={styles.inputContainer}>
              <FormInput
                value={description}
                onChangeText={setDescription}
                placeholder={t('voice.form.description.placeholder')}
                multiline
                numberOfLines={3}
                style={styles.descriptionInput}
              />
            </Box>

            <Text variant="label" color={colors.text.secondary}>
              {t('voice.form.voice.label')} *
            </Text>
            <VoiceSelector value={voiceId} onChange={setVoiceId} />
          </VStack>
        </Box>

        <Box style={styles.sectionCard}>
          <VStack spacing="sm">
            <HStack justify="between" align="center">
              <Text variant="label" color={colors.text.secondary}>
                {t('voice.form.script.label')} *
              </Text>
              <Text variant="caption" color={colors.text.tertiary}>
                {characterCount} / 5000 {t('voice.form.script.characters')}
              </Text>
            </HStack>

            <Box style={styles.variablesPanel}>
              <Text variant="caption" color={colors.text.tertiary} style={styles.variablesTitle}>
                {t('voice.form.script.insertVariables')}
              </Text>
              <HStack spacing="xs" style={styles.variableRow}>
                {TEMPLATE_VARIABLES.map((variable) => (
                  <Button
                    key={variable.value}
                    variant="secondary"
                    size="sm"
                    onPress={() => handleInsertVariable(variable.value)}
                    style={styles.variableChip}
                  >
                    <Text variant="caption" color={colors.text.primary}>
                      {variable.label}
                    </Text>
                  </Button>
                ))}
              </HStack>
            </Box>

            <Box style={styles.inputContainer}>
              <FormInput
                value={scriptTemplate}
                onChangeText={setScriptTemplate}
                placeholder={t('voice.form.script.placeholder')}
                multiline
                numberOfLines={7}
                maxLength={5000}
                style={styles.scriptInput}
              />
            </Box>
          </VStack>
        </Box>

        <Box style={styles.sectionCard}>
          <VStack spacing="md">
            <Text variant="label" color={colors.text.secondary}>
              {t('voice.form.settings.title')}
            </Text>

            <VStack spacing="sm">
              <HStack justify="between" align="center">
                <Text variant="bodySmall" color={colors.text.primary}>
                  {t('voice.form.settings.stability')}
                </Text>
                <Text variant="caption" color={colors.text.tertiary}>
                  {stability.toFixed(2)}
                </Text>
              </HStack>
              <SimpleSlider
                value={stability}
                onValueChange={(value) =>
                  setVoiceSettings((prev) => ({ ...prev, stability: value }))
                }
                minimumValue={0}
                maximumValue={1}
                step={0.01}
                minimumTrackTintColor={colors.primary.main}
                maximumTrackTintColor={colors.border.muted}
              />
            </VStack>

            <VStack spacing="sm">
              <HStack justify="between" align="center">
                <Text variant="bodySmall" color={colors.text.primary}>
                  {t('voice.form.settings.similarityBoost')}
                </Text>
                <Text variant="caption" color={colors.text.tertiary}>
                  {similarityBoost.toFixed(2)}
                </Text>
              </HStack>
              <SimpleSlider
                value={similarityBoost}
                onValueChange={(value) =>
                  setVoiceSettings((prev) => ({ ...prev, similarityBoost: value }))
                }
                minimumValue={0}
                maximumValue={1}
                step={0.01}
                minimumTrackTintColor={colors.primary.main}
                maximumTrackTintColor={colors.border.muted}
              />
            </VStack>

            <HStack justify="between" align="center" style={styles.toggleRow}>
              <Text variant="bodySmall" color={colors.text.primary}>
                {t('voice.form.settings.speakerBoost')}
              </Text>
              <Switch
                value={Boolean(voiceSettings.useSpeakerBoost)}
                onValueChange={(value) =>
                  setVoiceSettings((prev) => ({ ...prev, useSpeakerBoost: value }))
                }
                trackColor={{ false: colors.border.muted, true: colors.primary.main }}
                thumbColor={colors.text.inverse}
              />
            </HStack>

            <HStack justify="between" align="center" style={styles.toggleRow}>
              <Text variant="bodySmall" color={colors.text.primary}>
                {t('voice.form.settings.templateActive')}
              </Text>
              <Switch
                value={isActive}
                onValueChange={setIsActive}
                trackColor={{ false: colors.border.muted, true: colors.primary.main }}
                thumbColor={colors.text.inverse}
              />
            </HStack>
          </VStack>
        </Box>

        <Box style={styles.sectionCard}>
          <VStack spacing="sm">
            <Text variant="label" color={colors.text.secondary}>
              {t('voice.form.preview.title')}
            </Text>
            <VoicePreviewPlayer
              audioUrl={savedPreviewUrl}
              audioBlob={audioBlob}
              isLoading={isGeneratingPreview}
              onRetry={handlePreview}
            />
            <Button
              variant="secondary"
              onPress={handlePreview}
              disabled={!voiceId || !scriptTemplate || isGeneratingPreview}
            >
              <Text variant="body" color={colors.text.primary} style={styles.actionText}>
                {isGeneratingPreview && t('voice.form.preview.generating')}
                {!isGeneratingPreview &&
                  isEdit &&
                  templateId &&
                  t('voice.form.preview.generateAndSave')}
                {!isGeneratingPreview &&
                  !(isEdit && templateId) &&
                  t('voice.form.preview.generate')}
              </Text>
            </Button>
          </VStack>
        </Box>

        <HStack spacing="md" style={[styles.actionsRow, !isDesktop && styles.actionsRowMobile]}>
          <Button
            variant="secondary"
            onPress={onCancel}
            style={[styles.actionButton, !isDesktop && styles.actionButtonMobile]}
          >
            <Text variant="body" color={colors.text.primary}>
              {t('voice.form.submit.cancel')}
            </Text>
          </Button>

          <Button
            variant="primary"
            onPress={handleSubmit}
            disabled={!isValid || isSubmitting}
            style={[styles.actionButton, !isDesktop && styles.actionButtonMobile]}
          >
            <Text variant="body" color={colors.primary[900]} style={styles.actionText}>
              {isSubmitting && t('voice.form.submit.saving')}
              {!isSubmitting && isEdit && t('voice.form.submit.update')}
              {!isSubmitting && !isEdit && t('voice.form.submit.create')}
            </Text>
          </Button>
        </HStack>
      </VStack>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.lg,
    paddingBottom: tokens.spacing['3xl'],
  },
  sectionCard: {
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.cardOverlay,
    padding: tokens.spacing.lg,
  },
  inputContainer: {
    backgroundColor: colors.background.input,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.input,
    paddingHorizontal: tokens.spacing.md,
  },
  input: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    minHeight: 46,
  },
  descriptionInput: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    minHeight: 92,
    textAlignVertical: 'top',
    paddingTop: tokens.spacing.sm,
    paddingBottom: tokens.spacing.sm,
  },
  scriptInput: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    minHeight: 190,
    textAlignVertical: 'top',
    paddingTop: tokens.spacing.sm,
    paddingBottom: tokens.spacing.sm,
  },
  variablesPanel: {
    backgroundColor: colors.surface.cardOverlay,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    padding: tokens.spacing.sm,
    marginBottom: tokens.spacing.sm,
  },
  variablesTitle: {
    marginBottom: tokens.spacing.sm,
  },
  variableRow: {
    flexWrap: 'wrap',
    rowGap: tokens.spacing.xs,
  },
  variableChip: {
    marginBottom: tokens.spacing.xs,
  },
  sliderAdjustButton: {
    width: 30,
    height: 30,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.cardOverlay,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sliderTrackOuter: {
    flex: 1,
    height: 8,
    borderRadius: 999,
    overflow: 'hidden',
    flexDirection: 'row',
  },
  sliderTrackInner: {
    height: '100%',
  },
  sliderTrackRemainder: {
    height: '100%',
  },
  toggleRow: {
    borderTopWidth: 1,
    borderTopColor: colors.border.subtle,
    paddingTop: tokens.spacing.md,
  },
  actionsRow: {
    marginTop: tokens.spacing.sm,
  },
  actionsRowMobile: {
    flexDirection: 'column',
  },
  actionButton: {
    flex: 1,
  },
  actionButtonMobile: {
    width: '100%',
  },
  actionText: {
    fontWeight: '600',
  },
});
